import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import AuthenticationConfEditStore from './AuthenticationConfEditStore';
import AuthConfCreateStore from './AuthConfCreateStore';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import AsyncActionStore from '../AsyncActionStore';
import { apiCall } from '../../API';
import { objectDiff } from '../../diff';
import debug, { DEBUG } from '../../debug';

const logger = debug.create('AuthConfStore', DEBUG);

export default class AuthenticationConfigurationStore {
    static EMPTY_KEY = 'No SCIM API Key found.';
    static TENANT_URL_DEFAULT = 'URL will be generated after configuration is saved';
    static GENERIC_FAILURE_MSG = 'Your request could not be processed. Please try again.';


    static PropType = PropTypes.shape({
        editModal: FlagStore.PropType.isRequired,
        createModal: FlagStore.PropType.isRequired,
        deleteModal: FlagStore.PropType.isRequired,
        confirmDelete: FlagStore.PropType.isRequired,
        editStore: AuthenticationConfEditStore.PropType.isRequired,
        isConfEnabled: PropTypes.bool.isRequired,
        idProviderURL: PropTypes.string.isRequired,
        idProviderIssuer: PropTypes.string.isRequired,
        X_Certificate: PropTypes.string.isRequired,
        ssoURL: InputStore.PropType.isRequired,
        recipientURL: InputStore.PropType.isRequired,
        destinationURL: InputStore.PropType.isRequired,
        audienceURL: InputStore.PropType.isRequired,
        apiKey: InputStore.PropType.isRequired,
        ldapURL: PropTypes.string,
        userName: PropTypes.string,
        password: PropTypes.string,
        userBaseDN: PropTypes.string,
        groupsBaseDN: PropTypes.string,
        usersFilter: PropTypes.string,
        groupsFilter: PropTypes.string,
        LDAP_Certificate: PropTypes.string,
        contextClass: PropTypes.string,
        provisioningType: PropTypes.string,
        ldapType: PropTypes.string,
        isSCIM: PropTypes.bool.isRequired,
        isLDAP: PropTypes.bool.isRequired,
        onCreateKeyClick: PropTypes.func.isRequired,
        onDeleteKeyClick: PropTypes.func.isRequired,
        onCloseCreateModal: PropTypes.func.isRequired,
        onCloseDeleteModal: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class AuthenticationConfigurationStore */ {
            initData: null,
            loadStatus: new AsyncActionStore(),
            apiLoad: apiCall('/console/meta/config/full'),
            setConfigurationStatus: new AsyncActionStore(),
            apiEnableConfiguration: apiCall('/console/meta/enable'),
            apiSetExternalIdp: apiCall('/console/meta/idp/external'),
            apiSetInternalIdp: apiCall('/console/meta/idp/create'),
            apiUpdateInternalIdp: apiCall('/console/meta/idp/update'),
            deleteIDPStatus: new AsyncActionStore(),
            apiDeleteIDP: apiCall('/console/meta/idp/delete'),
            createTenantStatus: new AsyncActionStore(),
            apiCreateTenant: apiCall('/console/meta/connect'),
            generateKeyStatus: new AsyncActionStore(),
            apiGenerateKey: apiCall('/console/meta/api_key/create'),
            deleteKeyStatus: new AsyncActionStore(),
            apiDeleteKey: apiCall('/console/meta/api_key/delete'),
            apiResetIdpConfiguration: apiCall('/console/meta/reset/anomaly'),
            resetIdpConfigurationStatus: new AsyncActionStore(),
            editModal: new FlagStore(),
            createModal: new FlagStore(),
            deleteModal: new FlagStore(),
            confirmDelete: new FlagStore(),
            confirmResetIDP: new FlagStore(),
            resetIdpModal: new FlagStore(),
            promptPopUp: new FlagStore(),
            editStore: new AuthenticationConfEditStore(),
            createStore: new AuthConfCreateStore(),
            isConfEnabled: false,
            idProviderURL: '',
            idProviderIssuer: '',
            X_Certificate: '',
            contextClass: '',
            provisioningType: '',
            ldapType: '',
            ldapURL: '',
            userName: '',
            password: '',
            userBaseDN: '',
            groupsBaseDN: '',
            usersFilter: '',
            groupsFilter: '',
            LDAP_Certificate: '',
            ssoURL: new InputStore(),
            recipientURL: new InputStore(),
            destinationURL: new InputStore(),
            audienceURL: new InputStore(),
            apiKey: new InputStore({ value: AuthenticationConfigurationStore.EMPTY_KEY }),
            configuredApiKey: '',
            secret: '',
            tenantURL: new InputStore(),
            authType: '',
            metaExternalIdpName: '',
            justInTime: false,
            idpAssumeOwnership: false,
            hasExistingAccount: true,
            isSSOTenantConfigured: true,
            confSSOTenantErrorMsg: '',
            anomalyDetectedInternalIdp: false,
            anomalyDetectedExternalIdp: false,
            anomalyDetectedApiKey: false,

            get hasSamlConnectionError() {
                return this.anomalyDetectedInternalIdp || this.anomalyDetectedExternalIdp || this.anomalyDetectedApiKey;
            },

            get isSSOTenantGotError() {
                return this.confSSOTenantErrorMsg.length > 0;
            },
            get canDisplayFields() {
                return this.isSSOTenantConfigured && this.editStore.isConfEnabled.isTrue;
            },
            get isSCIM() {
                return this.provisioningType === AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM;
            },
            get isJIT() {
                return this.provisioningType === AuthenticationConfEditStore.PROVISIONING_TYPE.JIT;
            },
            get isLDAP() {
                return this.provisioningType === AuthenticationConfEditStore.PROVISIONING_TYPE.LDAP;
            },
            get hasKey() {
                return this.apiKey.value !== AuthenticationConfigurationStore.EMPTY_KEY && this.apiKey.value !== '';
            },
            get isOwnIDP() {
                return this.authType === AuthenticationConfEditStore.AUTH_TYPE.IDP;
            },
            get isExternalIDP() {
                return this.authType === AuthenticationConfEditStore.AUTH_TYPE.META;
            },
            get ProvisioningTypeDisplayName() {
                if (this.isJIT) {
                    return 'Just In Time (JIT)';
                }
                if (this.isLDAP) {
                    return 'LDAP AccessBridge';
                }
                return this.provisioningType;
            }
        }, data);
    }

    constructTenantURL = (id) => `https://api.metanetworks.com/v1/scim/${id}`;

    /**
     * @param data.generalConfig.metaEnabled - Shows if Configuration is Enabled
     * @param data.generalConfig.externalIdpConfig - if External IDP configured
     * @param data.generalConfig.metaExternalIdpId - External IDP ID
     * @param data.generalConfig.metaApiKeyId - Generated API Key
     * @param data.idpList - List of External IDPs
     * @param data.internalIdp.idp_response - Object that holds SCIM configuration
     * @param data.internalIdp.directory_response - Object that holds SCIM configuration
     * @param data.hasAccount - client have existing Meta account
     * @param data.ssoUrls - Customer SSO URL implementation info
     * @param {string} data.ssoUrls.ssoUrl
     * @param {string} data.ssoUrls.audienceUrl
     * @param {string} data.ssoUrls.destinationUrl
     * @param {string} data.ssoUrls.recipientUrl
     * */
    convertToFlatDataObj(data) {
        const externalIDP = data.idpList
            .find((i) => i.idp_response.id === data.generalConfig.metaExternalIdpId);
        const metaExternalIdpName = externalIDP ? externalIDP.idp_response.name : '';
        const hasIDPs = data.idpList.length > 0;
        const hasMetaAcc = data.generalConfig.hasExistingAccount !== undefined ?
            data.generalConfig.hasExistingAccount : false;

        let defaultValues = {
            provisioningType: '',
            contextClass: '',
            xCertificate: '',
            idProviderIssuer: '',
            idProviderURL: '',
            isAlreadyConfiguredIDP: false,
            tenantURL: AuthenticationConfigurationStore.TENANT_URL_DEFAULT,
            ldapType: '',
            ldapURL: '',
            userName: '',
            password: '',
            userBaseDN: '',
            groupsBaseDN: '',
            usersFilter: '',
            groupsFilter: '',
            LDAP_Certificate: '',
            configuredApiKey: '',
            justInTime: !hasIDPs,
            idpAssumeOwnership: !hasIDPs,
            hasExistingAccount: hasMetaAcc,
        };

        if (data.internalIdp && data.internalIdp.idp_response) {
            const samlConf = data.internalIdp.idp_response.saml_config;
            const scimConf = data.internalIdp.idp_response.scim_config;
            const isKeyExist = !!scimConf.api_key_id;
            defaultValues = Object.assign(defaultValues, {
                provisioningType: isKeyExist ? AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM
                    : AuthenticationConfEditStore.PROVISIONING_TYPE.JIT,
                contextClass: String(samlConf.authn_context_class),
                xCertificate: samlConf.certificate,
                idProviderIssuer: samlConf.issuer,
                idProviderURL: samlConf.sso_url,
                tenantURL: this.constructTenantURL(data.internalIdp.idp_response.id),
                isAlreadyConfiguredIDP: true,
                configuredApiKey: data.internalIdp.idp_response.scim_config.api_key_id,
                justInTime: samlConf.jit_enabled !== undefined ? samlConf.jit_enabled : defaultValues.justInTime,
                idpAssumeOwnership: scimConf.assume_ownership !== undefined ?
                    scimConf.assume_ownership : defaultValues.idpAssumeOwnership,
            });
        }

        if (data.internalIdp && data.internalIdp.directory_response) {
            const ldapConf = data.internalIdp.directory_response.ldap_provisioning_config;
            defaultValues = Object.assign(defaultValues, {
                provisioningType: AuthenticationConfEditStore.PROVISIONING_TYPE.LDAP,
                ldapType: ldapConf.type,
                ldapURL: ldapConf.url,
                userName: ldapConf.username,
                userBaseDN: ldapConf.users_base_dn,
                groupsBaseDN: ldapConf.groups_base_dn ? ldapConf.groups_base_dn : '',
                usersFilter: ldapConf.users_filter ? ldapConf.users_filter : '',
                groupsFilter: ldapConf.groups_filter ? ldapConf.groups_filter : '',
                LDAP_Certificate: ldapConf.certificate ? ldapConf.certificate : '',
            });
        }
        return Object.assign({
            isConfEnabled: data.generalConfig.metaEnabled,
            apiKey: data.generalConfig.metaApiKeyId
                ? data.generalConfig.metaApiKeyId : AuthenticationConfigurationStore.EMPTY_KEY,
            authType: data.generalConfig.externalIdpConfig || hasMetaAcc ?
                AuthenticationConfEditStore.AUTH_TYPE.META : AuthenticationConfEditStore.AUTH_TYPE.IDP,
            externalIdpConfig: data.generalConfig.externalIdpConfig,
            metaExternalIdpName,
            metaExternalIdpId: data.generalConfig.metaExternalIdpId ? data.generalConfig.metaExternalIdpId : '',
            shortName: data.generalConfig.metaOrgShortname,
            idpList: data.idpList,
            linkSsoURL: data.ssoUrls?.ssoUrl,
            linkAudienceUrl: data.ssoUrls?.audienceUrl,
            linkDestinationUrl: data.ssoUrls?.destinationUrl,
            linkRecipientUrl: data.ssoUrls?.recipientUrl,
            anomalyDetectedInternalIdp: data.generalConfig.anomalyDetectedInternalIdp,
            anomalyDetectedExternalIdp: data.generalConfig.anomalyDetectedExternalIdp,
            anomalyDetectedApiKey: data.generalConfig.anomalyDetectedApiKey,

        }, defaultValues);
    }

    load = action(() => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiLoad()
                .then(action((res) => {
                    try {
                        this.setInitialData(res);
                    } catch (e) {
                        logger.error(e.message);
                    }
                })));
        }
        return this.loadStatus.promise;
    });


    setInitialData = action((data) => {
        this.initData = this.convertToFlatDataObj(data);
        this.isConfEnabled = this.initData.isConfEnabled;
        this.hasExistingAccount = this.initData.hasExistingAccount;
        this.authType = this.initData.authType;
        this.metaExternalIdpName = this.initData.metaExternalIdpName;
        this.apiKey.setValue(this.initData.apiKey);

        this.ssoURL.setValue(this.initData.linkSsoURL);
        this.recipientURL.setValue(this.initData.linkRecipientUrl);
        this.destinationURL.setValue(this.initData.linkDestinationUrl);
        this.audienceURL.setValue(this.initData.linkAudienceUrl);

        this.justInTime = this.initData.justInTime;
        this.idpAssumeOwnership = this.initData.idpAssumeOwnership;

        this.provisioningType = this.initData.provisioningType;
        this.contextClass = this.initData.contextClass;
        this.X_Certificate = this.initData.xCertificate;
        this.idProviderIssuer = this.initData.idProviderIssuer;
        this.idProviderURL = this.initData.idProviderURL;
        this.tenantURL.setValue(this.initData.tenantURL);
        this.ldapType = this.initData.ldapType;
        this.ldapURL = this.initData.ldapURL;
        this.userName = this.initData.userName;
        this.password = this.initData.password;
        this.userBaseDN = this.initData.userBaseDN;
        this.groupsBaseDN = this.initData.groupsBaseDN;
        this.usersFilter = this.initData.usersFilter;
        this.groupsFilter = this.initData.groupsFilter;
        this.LDAP_Certificate = this.initData.LDAP_Certificate;

        this.configuredApiKey = this.initData.configuredApiKey;
        this.anomalyDetectedInternalIdp = this.initData.anomalyDetectedInternalIdp;
        this.anomalyDetectedExternalIdp = this.initData.anomalyDetectedExternalIdp;
        this.anomalyDetectedApiKey = this.initData.anomalyDetectedApiKey;
    });

    openEditModal = action(() => {
        this.editStore.Clear();
        if (this.isSSOTenantConfigured) {
            this.editStore.setValue(this.initData);
        } else {
            this.editStore.isConfEnabled.setFalse();
        }
        this.editModal.open();
    });

    checkShowCancelPrompt = action(() => {
        if (this.editStore.hasChanged() && this.editStore.data !== null) {
            this.promptPopUp.show();
        } else {
            this.editModal.close();
        }
    });

    getSAMLPrevConfig = () => {
        return {
            sso_url: this.initData.idProviderURL,
            issuer: this.initData.idProviderIssuer,
            certificate: this.initData.xCertificate,
            authn_context_class: this.initData.contextClass === 'null' ? null : this.initData.contextClass,
            jit_enabled: this.initData.justInTime,
        };
    };

    getSAMLCurrentConfig = () => {
        const contextClass = this.editStore.contextClass.getFirstValueOrEmpty();
        return {
            sso_url: this.editStore.idProviderURL.value, // Identity Provider SSO URL
            issuer: this.editStore.idProviderIssuer.value,
            certificate: this.editStore.X_Certificate.value,
            authn_context_class: contextClass === 'null' ? null : contextClass,
            jit_enabled: this.editStore.justInTime.value,
        };
    };

    getLDAPPrevConfig = () => ({
        type: this.initData.ldapType,
        url: this.initData.ldapURL,
        username: this.initData.userName,
        password: this.initData.password,
        users_base_dn: this.initData.userBaseDN,
        groups_base_dn: this.initData.groupsBaseDN ? this.initData.groupsBaseDN : null,
        users_filter: this.initData.usersFilter ? this.initData.usersFilter : null,
        groups_filter: this.initData.groupsFilter ? this.initData.groupsFilter.value : null,
        certificate: this.initData.LDAP_Certificate ? this.initData.LDAP_Certificate : null,
    });

    getLDAPCurrentConfig = () => ({
        type: this.editStore.ldapType.getFirstValueOrEmpty(),
        url: this.editStore.ldapURL.value,
        username: this.editStore.userName.value,
        password: this.editStore.password.value,
        users_base_dn: this.editStore.userBaseDN.value,
        groups_base_dn: this.editStore.groupsBaseDN.value ? this.editStore.groupsBaseDN.value : null,
        users_filter: this.editStore.usersFilter.value ? this.editStore.usersFilter.value : null,
        groups_filter: this.editStore.groupsFilter.value ? this.editStore.groupsFilter.value : null,
        certificate: this.editStore.LDAP_Certificate.value ? this.editStore.LDAP_Certificate.value : null,
    });

    onEditModalSave = action(() => {
        if (!this.setConfigurationStatus.isLoading) {
            if (!this.editStore.isConfEnabled.isTrue) {
                logger.log('Going to disable configuration ');
                const dto = { value: this.editStore.isConfEnabled.isTrue };
                this.setConfigurationStatus.setLoading(this.apiEnableConfiguration(dto)
                    .then((response) => {
                        this.isConfEnabled = this.editStore.isConfEnabled.isTrue;
                        this.initData.isConfEnabled = this.editStore.isConfEnabled.isTrue;
                        logger.log('Disable configuration: ', response);
                    }));
            } else if (this.editStore.authType.value === AuthenticationConfEditStore.AUTH_TYPE.META) {
                const dto = {
                    id: this.editStore.idpConfiguration.getFirstValueOrEmpty(),
                    name: this.editStore.idpConfiguration.getLabelForSelectedValue(),
                };
                this.setConfigurationStatus.setLoading(this.apiSetExternalIdp(dto)
                    .then(() => {
                        this.isConfEnabled = this.editStore.isConfEnabled.isTrue;
                        this.initData.isConfEnabled = this.editStore.isConfEnabled.isTrue;
                        this.authType = this.editStore.authType.value;
                        this.initData.authType = this.authType;
                        this.metaExternalIdpName = this.editStore.idpConfiguration.getLabelForSelectedValue();
                        this.initData.metaExternalIdpName = this.metaExternalIdpName;
                        this.initData.metaExternalIdpId = dto.id;
                        this.editModal.close();
                    }));
            } else if (this.editStore.isOwnIDP) {
                const prevConfiguration = this.getSAMLPrevConfig();
                const currentConfiguration = this.getSAMLCurrentConfig();
                const diff = objectDiff(currentConfiguration, prevConfiguration);
                let dto = {};
                const confOfSCIM = {};

                if (Object.keys(diff).length > 0) {
                    dto = {
                        idp_request: {
                            saml_config: diff,
                        },
                    };
                }

                if (this.editStore.isSCIM && this.configuredApiKey !== this.apiKey.value) {
                    confOfSCIM.api_key_id = this.apiKey.value;
                }

                if ((this.editStore.isSCIM || this.editStore.isJIT) &&
                    (this.initData.idpAssumeOwnership !== this.editStore.idpAssumeOwnership.value)) {
                    confOfSCIM.assume_ownership = this.editStore.idpAssumeOwnership.value;
                }

                if (Object.keys(confOfSCIM).length > 0) {
                    dto.idp_request = Object.assign({}, dto.idp_request, {
                        scim_config: confOfSCIM,
                    });
                }

                if (this.editStore.isLDAP) {
                    const prevLDAP = this.getLDAPPrevConfig();
                    const currentLDAP = this.getLDAPCurrentConfig();
                    const diffLDAP = objectDiff(currentLDAP, prevLDAP);
                    dto = Object.assign(dto, {
                        directory_request: {
                            ldap_provisioning_config: diffLDAP,
                        },
                    });
                }

                if (this.initData.isAlreadyConfiguredIDP) {
                    this.setConfigurationStatus.setLoading(this.apiUpdateInternalIdp(dto)
                        .then((response) => {
                            this.setConfiguredFields(response);
                            logger.log('Update Own IdP server result', response);
                        }));
                } else {
                    if (this.editStore.isSCIM || this.editStore.isJIT) {
                        dto.provisioning_type = this.editStore.provisioningType.getFirstValueOrEmpty();
                        dto.idp_request.saml_config.jit_enabled = this.editStore.justInTime.value;
                        dto.idp_request.scim_config = {
                            api_key_id: this.editStore.isJIT ? null : this.apiKey.value,
                            assume_ownership: this.editStore.idpAssumeOwnership.value,
                        };
                    }
                    this.setConfigurationStatus.setLoading(this.apiSetInternalIdp(dto)
                        .then((response) => {
                            this.setConfiguredFields(response);
                            logger.log('Create Own IdP server result', response);
                        }));
                }
            }
        }
        return this.setConfigurationStatus.promise;
    });

    setConfiguredFields = (response) => {
        logger.log('Going to set setConfiguredFields', response, 'init data', this.initData);

        this.isConfEnabled = this.editStore.isConfEnabled.isTrue;
        this.initData.isConfEnabled = this.isConfEnabled;

        this.authType = this.editStore.authType.value;
        this.initData.authType = this.authType;

        // Users Provisioning
        this.provisioningType = this.editStore.provisioningType.getFirstValueOrEmpty();
        this.initData.provisioningType = this.provisioningType;

        this.isAlreadyConfiguredIDP = true;
        this.initData.isAlreadyConfiguredIDP = true;

        // Identity Provider
        const samlConfig = response.idp_response.saml_config;
        this.idProviderURL = samlConfig.sso_url;
        this.initData.idProviderURL = this.idProviderURL;
        this.idProviderIssuer = samlConfig.issuer;
        this.initData.idProviderIssuer = this.idProviderIssuer;
        this.X_Certificate = samlConfig.certificate;
        this.initData.xCertificate = this.X_Certificate;
        this.contextClass = String(samlConfig.authn_context_class);
        this.initData.contextClass = this.contextClass;
        this.justInTime = samlConfig.jit_enabled;
        this.initData.justInTime = this.justInTime;

        if (response.idp_response && response.idp_response.id) {
            this.tenantURL.setValue(this.constructTenantURL(response.idp_response.id));
        }

        if (response.idp_response && response.idp_response.scim_config) {
            this.configuredApiKey = response.idp_response.scim_config.api_key_id;
            this.idpAssumeOwnership = response.idp_response.scim_config.assume_ownership;
            this.initData.idpAssumeOwnership = this.idpAssumeOwnership;
        }

        // LDAP
        this.ldapType = this.editStore.ldapType.getFirstValueOrEmpty();
        this.initData.ldapType = this.ldapType;
        this.ldapURL = this.editStore.ldapURL.value;
        this.initData.ldapURL = this.ldapURL;
        this.userName = this.editStore.userName.value;
        this.initData.userName = this.editStore.userName.value;
        this.password = this.editStore.password.value;
        this.initData.password = '';
        this.userBaseDN = this.editStore.userBaseDN.value;
        this.initData.userBaseDN = this.editStore.userBaseDN.value;
        this.groupsBaseDN = this.editStore.groupsBaseDN.value;
        this.initData.groupsBaseDN = this.editStore.groupsBaseDN.value;
        this.usersFilter = this.editStore.usersFilter.value;
        this.initData.usersFilter = this.editStore.usersFilter.value;
        this.groupsFilter = this.editStore.groupsFilter.value;
        this.initData.groupsFilter = this.editStore.groupsFilter.value;
        this.LDAP_Certificate = this.editStore.LDAP_Certificate.value;
        this.initData.LDAP_Certificate = this.editStore.LDAP_Certificate.value;
    };

    onCreateKeyClick = action(() => {
        if (!this.generateKeyStatus.isLoading) {
            this.generateKeyStatus.setLoading(this.apiGenerateKey()
                .then((response) => {
                    logger.log('generate Key Response: ', response);
                    this.createStore.setValue(response.id, response.secret);
                    this.createModal.open();
                })
                .catch((error) => {
                    logger.error(error.message);
                    this.apiKey.setValue(AuthenticationConfigurationStore.EMPTY_KEY);
                    throw error;
                }));
        }

        return this.generateKeyStatus.promise;
    });

    onCloseCreateModal = action(() => {
        this.apiKey.setValue(this.createStore.apiKey.value);
        this.configuredApiKey = this.createStore.apiKey.value;
        this.initData.configuredApiKey = this.createStore.apiKey.value;
        this.secret = this.createStore.secret.value;
        this.createModal.close();
    });

    onDeleteKeyClick = action(() => {
        this.deleteModal.open();
    });

    onModalDeleteKeyClick = action(() => {
        if (!this.deleteKeyStatus.isLoading) {
            this.deleteKeyStatus.setLoading(this.apiDeleteKey()
                .then((response) => {
                    logger.log('delete API Key response', response);
                    this.apiKey.setValue(AuthenticationConfigurationStore.EMPTY_KEY);
                    if (this.tenantURL.value === '') {
                        this.tenantURL.setValue(AuthenticationConfigurationStore.TENANT_URL_DEFAULT);
                    }
                    this.secret = '';
                    this.onCloseDeleteModal();
                }));
        }
        return this.deleteKeyStatus.promise;
    });

    onCloseDeleteModal = action(() => {
        this.confirmDelete.setFalse();
        this.deleteModal.close();
    });

    clearReadOnlyPageAndInitData = action(() => {
        const hasIDPs = this.initData.idpList.length > 0;
        this.initData.isConfEnabled = false;
        this.initData.authType = AuthenticationConfEditStore.AUTH_TYPE.IDP;
        this.initData.externalIdpConfig = false;
        this.initData.apiKey = AuthenticationConfigurationStore.EMPTY_KEY;
        this.initData.tenantURL = AuthenticationConfigurationStore.TENANT_URL_DEFAULT;
        this.initData.isAlreadyConfiguredIDP = false;
        this.initData.provisioningType = AuthenticationConfEditStore.PROVISIONING_TYPE.SCIM;
        // SAML
        this.initData.idProviderURL = '';
        this.initData.idProviderIssuer = '';
        this.initData.xCertificate = '';
        this.initData.contextClass = '';
        this.initData.justInTime = !hasIDPs;
        // LDAP
        this.initData.ldapType = '';
        this.initData.ldapURL = '';
        this.initData.userName = '';
        this.initData.password = '';
        this.initData.userBaseDN = '';
        this.initData.groupsBaseDN = '';
        this.initData.usersFilter = '';
        this.initData.groupsFilter = '';
        this.initData.LDAP_Certificate = '';
        // EXTERNAL IDP
        this.initData.metaExternalIdpId = '';
        this.initData.metaExternalIdpName = '';


        this.isConfEnabled = false;
        this.authType = '';
        this.metaExternalIdpName = '';
        this.apiKey.setValue(AuthenticationConfigurationStore.EMPTY_KEY);
        this.idpAssumeOwnership = !hasIDPs;

        this.ssoURL.setValue(this.initData.linkSsoURL);
        this.recipientURL.setValue(this.initData.linkRecipientUrl);
        this.destinationURL.setValue(this.initData.linkDestinationUrl);
        this.audienceURL.setValue(this.initData.linkAudienceUrl);

        this.provisioningType = '';
        this.contextClass = '';
        this.X_Certificate = '';
        this.idProviderIssuer = '';
        this.idProviderURL = '';
        this.tenantURL.setValue(AuthenticationConfigurationStore.TENANT_URL_DEFAULT);
        this.ldapType = '';
        this.ldapURL = '';
        this.userName = '';
        this.password = '';
        this.userBaseDN = '';
        this.groupsBaseDN = '';
        this.usersFilter = '';
        this.groupsFilter = '';
        this.LDAP_Certificate = '';
        this.anomalyDetectedExternalIdp = false;
        this.anomalyDetectedInternalIdp = false;
        this.anomalyDetectedApiKey = false;
    });

    onResetIDPClick = action(() => {
        this.confirmResetIDP.setFalse();
        this.resetIdpModal.open();
    });

    onResetIdpConfiguration = action(() => {
        if (!this.resetIdpConfigurationStatus.isLoading) {
            const data = {
                externalIdp: this.anomalyDetectedExternalIdp,
                internalIdp: this.anomalyDetectedInternalIdp,
                apiKey: this.anomalyDetectedApiKey,
            };
            this.resetIdpConfigurationStatus.setLoading(this.apiResetIdpConfiguration(data)
                .then(() => this.load()));
        }
        return this.resetIdpConfigurationStatus.promise;
    });

    deleteIDP = action(() => {
        if (!this.deleteIDPStatus.isLoading) {
            this.deleteIDPStatus.setLoading(this.apiDeleteIDP()
                .then(() => {
                    this.clearReadOnlyPageAndInitData();
                    this.editStore.clearAfterDeleteIDP();
                    this.resetIdpModal.close();
                }));
        }
        return this.deleteIDPStatus.promise;
    });

    enableTenant = action(() => {
        if (!this.createTenantStatus.isLoading) {
            this.confSSOTenantErrorMsg = '';
            this.createTenantStatus.setLoading(this.apiCreateTenant()
                .then(action((res) => {
                    try {
                        logger.info('enable tenant: setting init data');
                        this.setInitialData(res);
                        this.editStore.setValue(this.initData);
                        this.isSSOTenantConfigured = true;
                        this.editStore.isConfEnabled.setTrue();
                    } catch (e) {
                        logger.error('enable tenant init data error: ', e.message);
                    }
                    return true;
                }))
                .catch((err) => {
                    let message = '';
                    try {
                        const jsonErr = JSON.parse(err.message);
                        message = jsonErr.detail;
                    } catch (ignore) {
                        message = err.message;
                    }

                    logger.error('enable tenant error: ', message);
                    this.editStore.isConfEnabled.setFalse();
                    this.confSSOTenantErrorMsg = message.endsWith('.') ? message : `${message}.`;
                    return false;
                }));
        }
        return this.createTenantStatus.promise;
    });

    keyHasChanged = () => {
        return this.configuredApiKey !== this.apiKey.value;
    };

    setNotConfigured = () => {
        this.isSSOTenantConfigured = false;
    };
}
